import { Alert, Box, CircularProgress, Grid, Typography,  IconButton } from '@mui/material';
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { TimePicker } from '../components/TimePicker';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { GoogleMap } from '@react-google-maps/api';
import { Marker } from '@react-google-maps/api';
import { OverlayView } from '@react-google-maps/api';
import GoogleMapsReady from '../components/GoogleMapsReady';
import moment from "moment"
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import ReactDOM from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import { DataAPIContext } from '../api';
import { GET_ONE } from '../api/dataProvider';
import FixedLinearProgress from '../components/FixedLinearProgress';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';

// Utility function to convert coordinates to DMS format
const toDMS = (lat, lng) => {
    const convertToDMS = (value) => {
        const absValue = Math.abs(value);
        const degrees = Math.floor(absValue);
        const minutes = Math.floor((absValue - degrees) * 60);
        const seconds = ((absValue - degrees - minutes / 60) * 3600).toFixed(1);
        return `${degrees}°${minutes}'${seconds}"`;
    };

    const latDirection = lat >= 0 ? 'N' : 'S';
    const lngDirection = lng >= 0 ? 'E' : 'W';

    const latitudeDMS = `${convertToDMS(lat)}${latDirection}`;
    const longitudeDMS = `${convertToDMS(lng)}${lngDirection}`;

    return `${latitudeDMS} ${longitudeDMS}`;
};

const createMarkerIcon = (color) => ({
    url: `data:image/svg+xml;charset=UTF-8,
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
        <path fill="${encodeURIComponent(color)}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>`
});

const createCustomIconWithNumber = (color, number) => {
    const svgIcon = `
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
            <circle cx="20" cy="20" r="15" fill="${color}" stroke="black" stroke-width="2"/>
            <text x="20" y="25" font-size="14" text-anchor="middle" fill="white" font-weight="bold">${number}</text>
        </svg>
    `;
    return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgIcon)}`,
    };
};

const createCurrentLocationIcon = (color) => ({
    url: `data:image/svg+xml;charset=UTF-8,
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="8" fill="${encodeURIComponent(color)}" />
        <circle cx="12" cy="12" r="11" stroke="${encodeURIComponent(color)}" stroke-width="2" fill="none" opacity="0.5" />
    </svg>`,
});

const UserLegend = ({ users, pathColors, currentUser }) => {
    const sortedUsers = [...users].sort((a, b) => (a.id === currentUser.id ? -1 : b.id === currentUser.id ? 1 : 0));

    return (
        <div style={{
            position: 'absolute',
            bottom: '40px',
            left: '20px',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            padding: '10px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        }}>
            {sortedUsers.map((user) => (
                <div key={user.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="8" fill={pathColors[user.id]} />
                        <circle cx="12" cy="12" r="11" stroke={pathColors[user.id]} strokeWidth="2" fill="none" opacity="0.5" />
                    </svg>
                    <Typography pl={0.5} fontSize={16} fontWeight={700} variant="body2">{user.display_name}</Typography>
                </div>
            ))}
        </div>
    );
};

const calculateThreshold = (map) => {
    const zoom = map.getZoom();
    const baseThreshold = 0.5;
    const threshold = baseThreshold / Math.pow(2, zoom - 1);
    return threshold * 250;
};

const CustomTooltipMarker = ({ markerId, position, userName, date, time, coordinates, icon, color, map, activeMarker, setActiveMarker }) => {
    const [isTopEdge, setIsTopEdge] = useState(false);
    const isSmallScreen = !useIsWidthUp('sm');
    const overlayRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        if (map) {
            const handleBoundsChanged = () => {
                const bounds = map.getBounds();
                if (bounds) {
                    const northEast = bounds.getNorthEast();
                    const markerLat = position.lat;
                    let threshold = calculateThreshold(map);
                    const nearTopEdge = markerLat > northEast.lat() - threshold;
                    setIsTopEdge(nearTopEdge);
                }
            };

            const listener = map.addListener('bounds_changed', handleBoundsChanged);
            return () => {
                if (window.google && window.google.maps && window.google.maps.event) {
                    window.google.maps.event.removeListener(listener);
                } else {
                    map.removeListener(listener);
                }
            };
        }
    }, [map, position]);

    useEffect(() => {
        if (map && !isSmallScreen) {
            const handleClickOutside = (event) => {
                if (overlayRef.current && !overlayRef.current.contains(event.target)) {
                    setActiveMarker(null);
                    setIsHovered(false);
                }
            };

            const listener = map.addListener('click', handleClickOutside);

            return () => {
                if (listener) {
                    if (window.google && window.google.maps && window.google.maps.event) {
                        window.google.maps.event.removeListener(listener);
                    } else {
                        map.removeListener(listener);
                    }
                }
            };
        }
    }, [map, isSmallScreen, setActiveMarker]);

    const handleMarkerClick = (e) => {
        e.stop();
        if (isSmallScreen) {
            setActiveMarker(prev => prev === markerId ? null : markerId);
        } else {
            setActiveMarker(markerId);
        }
    };

    const handleMarkerMouseEnter = () => {
        if (!isSmallScreen) {
            setActiveMarker(markerId);
            setIsHovered(true);
        }
    };

    const handleMarkerMouseLeave = () => {
        if (!isSmallScreen) {
            setIsHovered(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (!isSmallScreen) {
            e.stopPropagation();
        }
    };

    const handleClose = (e) => {
        e.stopPropagation();
        if (isSmallScreen) {
            setActiveMarker(null);
        } else {
            setActiveMarker(null);
            setIsHovered(false);
        }
    };

    return (
        <>
            <Marker
                key={markerId}
                position={position}
                icon={icon}
                onClick={handleMarkerClick}
                onMouseOver={handleMarkerMouseEnter}
                onMouseOut={handleMarkerMouseLeave}
                clickable={true}
                options={{
                    optimized: false,
                    zIndex: (activeMarker === markerId || isHovered) ? 1000 : 1
                }}
            />
            {(activeMarker === markerId || (!isSmallScreen && isHovered)) && (
                <OverlayView
                    position={isSmallScreen ? map.getCenter() : position}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={(width, height) => ({
                        x: 0,
                        y: isTopEdge ? 20 : -(height + 50)
                    })}
                >
                    <div 
                        ref={overlayRef}
                        onClick={handleOverlayClick}
                        style={{
                            background: 'white',
                            padding: '10px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                            pointerEvents: 'auto',
                            position: 'relative',
                            width: isSmallScreen ? '90vw' : '300px',
                            maxWidth: '400px',
                            transform: isSmallScreen ? 'translate(-50%, -50%)' : (isTopEdge ? 'translate(0, 0)' : 'translate(0%, -10%)'),
                            transition: 'transform 0.2s ease-out',
                            overflow: 'auto'
                        }}
                    >
                        <Grid container alignContent="center" alignItems="center" justifyContent="space-between" style={{ width: '100%', borderBottom: "solid #D8D8D8 1px", marginBottom: '0.75rem', paddingBottom: '0.25rem' }} >
                            <Grid item xs={10}>
                                <Typography variant="body2" fontWeight="bold">{userName}</Typography>
                            </Grid>
                            <Grid item xs={2} container justifyContent="flex-end">
                                {isSmallScreen ? (
                                    <IconButton onClick={handleClose} size="small">
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 30 30" fill={color}>
                                        <circle cx="12" cy="12" r="8" />
                                    </svg>
                                )}
                            </Grid>
                        </Grid>
                        <Grid container gap={0.5} alignContent="center" >
                            <Typography variant="body2" fontWeight="bold">Date: </Typography>  <Typography variant="body2">{date}</Typography>
                        </Grid>
                        <Grid container gap={0.5} alignContent="center" >
                            <Typography variant="body2" fontWeight="bold">Time: </Typography>  <Typography variant="body2">{time}</Typography>
                        </Grid>
                        <Grid container gap={0.5} alignContent="center" >
                            <Typography variant="body2" fontWeight="bold">Coordinates: </Typography>  <Typography variant="body2">{coordinates}</Typography>
                        </Grid>
                    </div>
                </OverlayView>
            )}
        </>
    );
};

const defaultPathColors = {
    0: "#007AFF",
    1: "#EB5757",
    2: "#27AE60",
    3: "#9B51E0",
};


const mapStyles = [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
    { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'transit.station', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] }
];

const FullscreenControl = ({ map, isFullscreen, setIsFullscreen }) => {
  const mapContainerRef = useRef(null);

  useEffect(() => {
    if (map) {
      mapContainerRef.current = map.getDiv().parentElement;
    }
  }, [map]);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const toggleFullscreen = () => {
    setIsFullscreen(prev =>{
        let isNewState = prev;
        if (!map || !mapContainerRef.current) return; 
        const mapElement = map.getDiv();
        if (isIOS) {
          if (!isNewState) {
            mapElement.style.position = 'fixed';
            mapElement.style.top = '64px';
            mapElement.style.left = '0';
            mapElement.style.width = '100%';
            mapElement.style.height = 'calc(100vh - 64px)';
            mapElement.style.zIndex = '999999999999999999 !important';
          } else {
            mapElement.style.width = '100%';
            mapElement.style.position = 'relative';
            mapElement.style.height = 'calc(100vh - 30rem)';
            mapElement.style.top = '';
          }
          return !prev;
        } 
        if (!isNewState) {

    
        if (mapElement.requestFullscreen) {
            mapElement.requestFullscreen();
        } else if (mapElement.webkitRequestFullscreen) {
            mapElement.webkitRequestFullscreen();
        } else if (mapElement.msRequestFullscreen) {
            mapElement.msRequestFullscreen();
        }
        return !prev;
        } 
        
        if (document.exitFullscreen) {
        document.exitFullscreen();
    
            document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
        }
        return !prev
    });

  };

  if (!map) return null; 

  return (
    <div
      style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        background: 'white',
        padding: '5px',
        borderRadius: '2px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
        cursor: 'pointer',
      }}
      onClick={toggleFullscreen}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {isFullscreen ? (
          <>
            <polyline points="4 14 10 14 10 20"></polyline>
            <polyline points="20 10 14 10 14 4"></polyline>
            <line x1="14" y1="10" x2="21" y2="3"></line>
            <line x1="3" y1="21" x2="10" y2="14"></line>
          </>
        ) : (
          <>
            <polyline points="15 3 21 3 21 9"></polyline>
            <polyline points="9 21 3 21 3 15"></polyline>
            <line x1="21" y1="3" x2="14" y2="10"></line>
            <line x1="3" y1="21" x2="10" y2="14"></line>
          </>
        )}
      </svg>
    </div>
  );
};

const LocationHistoryMap = ({ user, currentTab }) => {
    const { settings } = useContext(ContextStore);
    const dataAPI = useContext(DataAPIContext);
    const history = useHistory();

    const [locationHistory, setLocationHistory] = useState(null);
    const [locationError, setLocationError] = useState(null);
    const [dateRange, setDateRange] = useState([null, null]);
    const [selectedUsersToDisplay, setSelectedUsersToDisplay] = useState([user]);
    const [pathColors, setPathColors] = useState(defaultPathColors);
    const paths = {};
    const [mapCenter, setMapCenter] = useState(paths[selectedUsersToDisplay[0]?.id]?.[0] || (user?.latest_position?.latitude && { lat: user?.latest_position?.latitude, lng: user?.latest_position?.longitude }) || { lat: 0, lng: 0 })
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [timeRange, setTimeRange] = useState([null, null]);
    const isSmallScreen = !useIsWidthUp('sm');
    const [map, setMap] = useState(null);
    const [activeMarker, setActiveMarker] = useState(null);
    const mapRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [stableLocations, setStableLocations] = useState({});

    if (locationHistory) {
        selectedUsersToDisplay.slice(0, 4).forEach((sel_user, index) => {
            paths[sel_user.id] = locationHistory[sel_user.id]?.locations.map(([time, lng, lat]) => ({ lat, lng })) || [];
        });
    }
    const hasLocations = locationHistory && Object.values(locationHistory).some(user => user.locations.length > 0);

    const updateURLParams = (params) => {
        const searchParams = new URLSearchParams(params);
        history.push({ search: searchParams.toString() });
    };

    const getDateWithTime = (date, time) => {
        const result = date.clone().startOf('day');
        if (time) {
            result.hour(time.hour()).minute(time.minute());
        }
        return result;
    };

    const fetchLocationHistory = async () => {
        if (!dateRange[0]) return;
        setLoading(true);

        const fromDate = getDateWithTime(dateRange[0], timeRange[0]).toISOString();
        let toDate = dateRange[1] 
            ? (timeRange[1] ? getDateWithTime(dateRange[1], timeRange[1]) : dateRange[1].endOf('day')).toISOString()
            : dateRange[0].clone().endOf('day').toISOString();

        const params = new URLSearchParams();

        currentTab && params.append('tab', currentTab);
        params.append('from', fromDate);
        params.append('to', toDate || moment().endOf('day').toISOString());
        if (timeRange[0]) params.append('startTime', timeRange[0].toISOString());
        if (timeRange[1]) params.append('endTime', timeRange[1].toISOString());
        const selectedUserIds = new Set(selectedUsersToDisplay.map(u => u.id));

        selectedUserIds.forEach(id => {
            params.append('user', id);
        });
        updateURLParams(params);
        params.delete('tab')
        params.delete('startTime')
        params.delete('endTime')
        const urlSuffix = `?${params.toString()}`;

        try {
            const response = await dataAPI(GET_ONE, 'user_location_history', { urlSuffix });
            const newLocationHistory = response.data?.users;

            if (!newLocationHistory) {
                setLocationHistory(null);
                setLoading(false);
                return;
            }

            setLocationHistory(newLocationHistory);
            const newPathColors = { [user.id]: "#007AFF" };
            let colorIndex = 1;

            Object.keys(newLocationHistory).forEach(userId => {
                if (parseInt(userId) !== user.id) {
                    newPathColors[userId] = defaultPathColors[(colorIndex) % Object.keys(defaultPathColors).length];
                    colorIndex++;
                }
            });
            setPathColors(newPathColors);

            const newCenter = Object.values(newLocationHistory).flatMap(user => user.locations)
                .map(location => ({ lat: location[2], lng: location[1] }))[0];

            if (newCenter) {
                setMapCenter(newCenter);
            }
            setLoading(false);

        } catch (error) {
            setLoading(false);
            setLocationError('An error occurred while fetching location history.');
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const from = searchParams.get('from');
        const to = searchParams.get('to');
        const users = searchParams.getAll('user');
        const startTime = searchParams.get('startTime');
        const endTime = searchParams.get('endTime');
        let existing_time_range = [null, null]
        if (startTime) {
            existing_time_range = [moment(startTime), timeRange[1]];
        }

        if (endTime) {
            if (existing_time_range[0]) {
                existing_time_range = [existing_time_range[0], moment(endTime)];
            } else {
                existing_time_range = [null, moment(endTime)];
            }
        }
        setTimeRange(existing_time_range)
        if (from && to) {
            setDateRange([moment(from), moment(to)]);
        }

        if (users.length) {
            const fetchUsers = async () => {
                try {
                    setLoading(true)
                    const userPromises = users.map(id => dataAPI(GET_ONE, 'user', { id }));
                    const userData = await Promise.all(userPromises);
                    setSelectedUsersToDisplay([...userData.map(res => res.data)]);
                } catch (error) {
                    enqueueSnackbar('Error fetching users', { variant: 'error' });
                } finally {
                    setLoading(false)
                }
            };
            fetchUsers();
        }
    }, []); 

    useEffect(() => {
        fetchLocationHistory();
    }, [selectedUsersToDisplay, dateRange, timeRange]);

    useEffect(() => {
        if (locationHistory) {
            const newStableLocations = {};
            Object.keys(locationHistory).forEach((userId) => {
                newStableLocations[userId] = locationHistory[userId].locations
                    .map((location, index) => ({
                        ...location,
                        originalIndex: index,
                        position: { lat: location[2], lng: location[1] }
                    }));
            });
            setStableLocations(newStableLocations);
        }
    }, [locationHistory]);

    const handleUserChange = (event, users) => {
        if (users.length > 4) {
            enqueueSnackbar('Max users to select is set to 4', { variant: 'error' });
            return;
        }
        setSelectedUsersToDisplay(prev => [...users, user]);
    };

    const handleDateChange = (newDateRange) => {
        let [newFrom, newTo] = newDateRange;
        
        // If only the "from" date is selected, set "to" date to the same date
        if (newFrom && !newTo) {
            newTo = newFrom.clone().endOf('day');
        }
        
        // Ensure "to" date is not before "from" date
        if (newFrom && newTo && newTo.isBefore(newFrom)) {
            newTo = newFrom.clone().endOf('day');
        }
        
        setDateRange([newFrom, newTo]);
    };

    const handleTimeChange = (newTimeRange) => {
        setTimeRange(newTimeRange);
    };

    const locationSharingNotAllowed = false; // [LOCATION_DENIED, LOCATION_NOT_REPORTED].includes(user.location_status);
    const containerStyles = {
        width: '100%',
        height: isSmallScreen ? 'calc(100vh - 30rem)' : 'calc(100vh - 13rem)',
    }

    return (
        <>
            <FixedLinearProgress visibility={loading} />
          {!isFullscreen &&   <Box p={isSmallScreen ? 1 : 2}>
                <Grid container spacing={isSmallScreen ? 1 : 2}>
                    <Grid item xs={12} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DateRangePicker
                                slots={{ field: SingleInputDateRangeField }}
                                disabled={locationSharingNotAllowed}
                                label="Date"
                                startText="Start Date"
                                endText="End Date"
                                value={dateRange}
                                onChange={handleDateChange}
                                slotProps={{
                                    textField: {
                                        size: 'large',
                                        variant: 'filled',
                                        fullWidth: true
                                    },
                                    actionBar: {
                                        actions: ['clear', 'accept'],
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={6} lg={1.5}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <TimePicker
                                label="Start Time"
                                disabled={locationSharingNotAllowed}
                                value={timeRange[0]}
                                onChange={(newValue) => handleTimeChange([newValue, timeRange[1]])}
                                slotProps={{
                                    textField: {
                                        size: 'large',
                                        variant: 'filled',
                                        fullWidth: true
                                    },
                                    actionBar: {
                                        actions: ['clear', 'accept'],
                                    },

                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={6} lg={1.5}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <TimePicker
                                label="End Time"
                                value={timeRange[1]}
                                disabled={locationSharingNotAllowed}
                                onChange={(newValue) => handleTimeChange([timeRange[0], newValue])}
                                slotProps={{
                                    textField: {
                                        size: 'large',
                                        variant: 'filled',
                                        fullWidth: true
                                    },
                                    actionBar: {
                                        actions: ['clear', 'accept'],
                                    },

                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} lg={7}>
                        <RemoteAutocomplete
                            multiple
                            disabled={locationSharingNotAllowed}
                            resource="user"
                            urlSuffix={null}
                            label="Add users"
                            variant="filled"
                            size="large"
                            fullWidth
                            resetQueryOnBlur
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {option.display_name || (option.first_name && (option.first_name + " " + option.last_name)) || option.email}
                                </li>
                            )} // making sure the display name is not being used as key
                            onChange={handleUserChange}
                            filterOptions={(options) => {
                                return options.filter(u => u.id !== user.id && selectedUsersToDisplay.filter(sel_user => sel_user.id === u.id).length === 0)
                            }} 
                            apiFilters={[
                                { field: 'fake_users', value: false }

                            ]}
                            getOptionLabel={(opt) => `${opt.display_name}`}
                            value={selectedUsersToDisplay.filter(u => u.id !== user?.id)}
                        />
                    </Grid>
                </Grid>
            </Box>}
            {!locationSharingNotAllowed ? <>
                {(!hasLocations || !dateRange[0]) ?
                    <GoogleMapsReady apiKey={settings.google_maps_key}>
                        <GoogleMap
                            mapContainerStyle={containerStyles}
                            center={mapCenter}
                            zoom={4}
                            options={{
                                fullscreenControl: false, 
                                styles: mapStyles,
                                clickableIcons: false,
                            }}
                            onLoad={(mapInstance) => {
                                setMap(mapInstance);
                                const fullscreenControlDiv = document.createElement('div');
                                ReactDOM.render(<FullscreenControl map={mapInstance} isFullscreen={isFullscreen} setIsFullscreen={setIsFullscreen} />, fullscreenControlDiv);
                                mapInstance.controls[window.google.maps.ControlPosition.TOP_RIGHT].push(fullscreenControlDiv);
                            }}
                            onClick={() => setActiveMarker(null)}
                        >
                        </GoogleMap>
                    </GoogleMapsReady> :
                    <>
                        {locationError ? (
                            <Alert severity="error">{locationError}</Alert>
                        ) : (
                            <>
                                {locationHistory ? (
                                    <>
                                        {dateRange[0] ?
                                                <GoogleMapsReady apiKey={settings.google_maps_key}>
                                                    <GoogleMap
                                                        mapContainerStyle={containerStyles}
                                                        center={mapCenter}
                                                        zoom={18}
                                                        options={{
                                                            fullscreenControl: false, 
                                                            styles: mapStyles,
                                                            clickableIcons: false,
                                                        }}
                                                        onLoad={(mapInstance) => {
                                                            setMap(mapInstance);
                                                            mapRef.current = mapInstance;
                                                        }}
                                                        onClick={() => setActiveMarker(null)}
                                                    >
                                                        {stableLocations && Object.keys(stableLocations).map((userId) => {
                                                            return stableLocations[userId].map((location, locIndex) => {
                                                                const position = location.position;
                                                                const markerId = `${userId}-${location.originalIndex}`;
                                                                
                                                                let icon = createCustomIconWithNumber(pathColors[userId], location.originalIndex + 1);
                                                                if (locIndex === 0) {
                                                                    icon = createCurrentLocationIcon(pathColors[userId]);
                                                                } else if (locIndex === stableLocations[userId].length - 1) {
                                                                    icon = createMarkerIcon(pathColors[userId]);
                                                                }

                                                                return (
                                                                    <CustomTooltipMarker
                                                                        key={markerId}
                                                                        markerId={markerId}
                                                                        position={position}
                                                                        userName={locationHistory[userId].display_name}
                                                                        date={moment.utc(location[0]).format('MM/DD/YYYY')}
                                                                        time={moment.utc(location[0]).format('h:mm:ss A Z')}
                                                                        coordinates={toDMS(position.lat, position.lng)}
                                                                        icon={icon}
                                                                        color={pathColors[userId]}
                                                                        map={map}
                                                                        activeMarker={activeMarker}
                                                                        setActiveMarker={setActiveMarker}
                                                                    />
                                                                );
                                                            });
                                                        })}
                                                     {map && <FullscreenControl map={map} isFullscreen={isFullscreen} setIsFullscreen={setIsFullscreen} />}
                                                    </GoogleMap>
                                                    <UserLegend users={selectedUsersToDisplay} pathColors={pathColors} currentUser={user} />
                                                </GoogleMapsReady>
                                                :
                                            <Typography p={2} pt={1}>Start date must be selected</Typography>}
                                    </>
                                ) : (
                                    <CircularProgress />
                                )}
                            </>
                        )}</>}</> : <div style={{
                            ...containerStyles, justifyContent: 'center', alignContent: "center", textAlign: 'center'
                        }}>
                <div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="86" height="87" viewBox="0 0 86 87" fill="none">
                        <g clip-path="url(#clip0_4529_24183)">
                            <path d="M43 86.5C66.7482 86.5 86 67.2482 86 43.5C86 19.7518 66.7482 0.5 43 0.5C19.2518 0.5 0 19.7518 0 43.5C0 67.2482 19.2518 86.5 43 86.5Z" fill="#EAF4FF" />
                            <path d="M77.2072 8.36328H14.2266V82.0724H77.2072V8.36328Z" fill="#AEDCFF" />
                            <path d="M74.4904 6.64648H11.5098V80.3557H74.4904V6.64648Z" fill="white" />
                            <line x1="30.7071" y1="32" x2="56" y2="57.2929" stroke="#007AFF" stroke-linecap="round" />
                            <line x1="0.5" y1="-0.5" x2="36.2696" y2="-0.5" transform="matrix(-0.707107 0.707107 0.707107 0.707107 56 32)" stroke="#007AFF" stroke-linecap="round" />
                        </g>
                        <defs>
                            <clipPath id="clip0_4529_24183">
                                <rect width="86" height="86" fill="white" transform="translate(0 0.5)" />
                            </clipPath>
                        </defs>
                    </svg>

                    <Typography > No location information is available for this user.</Typography>
                </div>
            </div>}
        </>
    );
};

export default LocationHistoryMap;